/**
 * Permissions Service — Mycelium Protocol Connector
 *
 * Gravitational centre for all permission checks.
 * Merges authority roles (user_roles) and functional roles (functional_roles)
 * into a unified permission context.
 *
 * Design Principles:
 * - Economy of Movement: One call resolves everything
 * - Holographic Universe: Context carries the full picture
 * - Gravitational Organisation: All checks orbit this service
 *
 * @module permissions-service
 * @since 2026-02-21
 */

import {
  Permission,
  PermissionContext,
  resolvePermissions,
  hasPermission as staticHasPermission,
  hasAnyPermission as staticHasAnyPermission,
  hasAllPermissions as staticHasAllPermissions,
} from '@flowstate/types';
import { logger } from '../lib/logger';
import type { IService } from './service-registry';

// ============================================================================
// CONFIGURATION
// ============================================================================

/** Cache TTL in milliseconds (5 minutes — matches EnhancedAuthService) */
const CONTEXT_TTL_MS = 5 * 60 * 1000;

// ============================================================================
// SERVICE
// ============================================================================

export class PermissionsService implements IService {
  readonly name = 'PermissionsService';
  private contextCache = new Map<string, PermissionContext>();
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    logger.info('🔐 PermissionsService initialized');
  }

  async dispose(): Promise<void> {
    this.contextCache.clear();
    this.initialized = false;
    logger.info('🔐 PermissionsService disposed');
  }

  /**
   * Get the full permission context for a user.
   * Holographic — each call returns the complete picture.
   *
   * @param userId - User ID to resolve
   * @param authorityRoles - Roles from user_roles table (already fetched)
   * @param functionalRoles - Optional functional role slugs
   * @param orgId - Optional organization context
   * @param teamId - Optional team context
   */
  getPermissionContext(
    userId: string,
    authorityRoles: string[],
    functionalRoles: string[] = [],
    orgId?: string,
    teamId?: string,
  ): PermissionContext {
    // Check cache first (economy of movement)
    const cached = this.contextCache.get(userId);
    if (cached && (Date.now() - cached.resolvedAt) < CONTEXT_TTL_MS) {
      return cached;
    }

    // Resolve all permissions by merging authority + functional roles
    const allRoles = [...authorityRoles, ...functionalRoles];
    const permissions = resolvePermissions(allRoles);

    const context: PermissionContext = {
      userId,
      authorityRoles,
      functionalRoles,
      permissions,
      organizationId: orgId,
      teamId,
      resolvedAt: Date.now(),
    };

    // Cache for later use
    this.contextCache.set(userId, context);

    return context;
  }

  /**
   * Check if a user can perform a specific action.
   * Economy of movement — one call, one answer.
   */
  can(roles: string[], permission: Permission): boolean {
    return staticHasPermission(roles, permission);
  }

  /**
   * Check if a user can perform ANY of the specified actions.
   */
  canAny(roles: string[], permissions: Permission[]): boolean {
    return staticHasAnyPermission(roles, permissions);
  }

  /**
   * Check if a user can perform ALL of the specified actions.
   */
  canAll(roles: string[], permissions: Permission[]): boolean {
    return staticHasAllPermissions(roles, permissions);
  }

  /**
   * Invalidate cached context for a user.
   * Call when roles change.
   */
  invalidateContext(userId: string): void {
    this.contextCache.delete(userId);
  }

  /**
   * Clear all cached contexts.
   */
  clearAllContexts(): void {
    this.contextCache.clear();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const permissionsService = new PermissionsService();
